import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { CheckCircle2, AlertTriangle } from "lucide-react";
import { journeyApi } from "@/lib/journeyApi";
import { JourneyTopBar, JourneyFooter } from "@/components/JourneyChrome";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token");
  const [status, setStatus] = useState("checking"); // checking | verified | failed
  const done = useRef(false);

  useEffect(() => {
    if (done.current) return;
    done.current = true;
    (async () => {
      if (!token) { setStatus("failed"); return; }
      try {
        await journeyApi.verifyEmail(token);
        setStatus("verified");
      } catch {
        setStatus("failed");
      }
    })();
  }, [token]);

  return (
    <div className="flex min-h-screen flex-col bg-[#FAF8F4] text-[#171717]" data-testid="verify-email-page">
      <JourneyTopBar showSignIn={false} />
      <main className="flex flex-1 items-center justify-center px-5 py-12">
        <div className="w-full max-w-md text-center">
          {status === "checking" && (
            <div data-testid="verify-checking">
              <div className="mx-auto h-9 w-9 animate-spin rounded-full border-2 border-[#E5E0D9] border-t-[#2F6B57]" />
              <p className="mt-5 text-[15px] text-[#525252]">Confirming your email…</p>
            </div>
          )}
          {status === "verified" && (
            <div className="rounded-2xl pe-surface p-8" data-testid="verify-success">
              <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#DDE7DF] text-[#164C3B]">
                <CheckCircle2 className="h-7 w-7" />
              </span>
              <h1 className="mt-5 font-serif text-2xl text-[#171717]">Email confirmed</h1>
              <p className="mt-2 text-[15px] leading-relaxed text-[#525252]">Your email address is verified. You're all set.</p>
              <button
                type="button"
                onClick={() => navigate("/login", { replace: true })}
                data-testid="verify-to-login"
                className="mt-5 inline-flex min-h-[48px] items-center justify-center rounded-full bg-[#2F6B57] px-7 text-sm font-semibold text-white transition-colors hover:bg-[#255544]"
              >
                Go to sign in
              </button>
            </div>
          )}
          {status === "failed" && (
            <div className="rounded-2xl pe-surface p-8" data-testid="verify-failed">
              <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#F3E4DE] text-[#8B3A2B]">
                <AlertTriangle className="h-7 w-7" />
              </span>
              <h1 className="mt-5 font-serif text-2xl text-[#171717]">This link is invalid or expired</h1>
              <p className="mt-2 text-[15px] leading-relaxed text-[#525252]">
                The verification link may have already been used or has expired.
              </p>
              <Link
                to="/login"
                data-testid="verify-failed-login"
                className="mt-5 inline-flex min-h-[48px] items-center justify-center rounded-full border border-[#2F6B57] px-7 text-sm font-semibold text-[#2F6B57] transition-colors hover:bg-[#DDE7DF]"
              >
                Back to sign in
              </Link>
            </div>
          )}
        </div>
      </main>
      <JourneyFooter />
    </div>
  );
}
